/**
 * oddit-header — Interactive Header Behavior
 * Handles: mobile menu toggle, desktop mega menu dropdowns, search drawer
 * Cart opens via the legacy hybrid cart (data-ns-hybrid-cart-open).
 */
(function () {
  'use strict';

  var customSelectGlobalListenersBound = false;
  var customSelectReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initHeader(container) {
    var header = container || document.querySelector('.oddit-header');
    if (!header) return;

    /* ---------- Search Header Spacing ----------
       Search button clicks are handled by gSearch (in js-main.js); its body
       click delegation already binds to .js-search-toggle. We only update
       --s-header-space here, and only when the oddit header is actually the
       visible header (otherwise we'd clobber the value gHeader sets for the
       legacy .g-header).
    */
    var headerSpaceRaf = null;
    var lastHeaderSpace = null;
    var headerGroup = document.querySelector('.shopify-section-group-header-group');
    var headerInner = header.querySelector('.oddit-header__inner');
    var menuToggle = header.querySelector('.oddit-header__menu-toggle');
    var mobileMenu = header.querySelector('.oddit-header__mobile-menu');
    var mobileOverlay = header.querySelector('.oddit-header__mobile-menu-overlay');
    var mobileRootPanel = header.querySelector('[data-mobile-panel="root"]');
    var mobileChildPanels = header.querySelectorAll('.oddit-header__mobile-panel--child');
    var mobilePanelTriggers = header.querySelectorAll('[data-mobile-panel-trigger]');
    var mobilePanelBackButtons = header.querySelectorAll('[data-mobile-panel-back]');
    var lastMobilePanelTrigger = null;

    /* Header menu images render with loading="lazy" so they don't compete with
       above-the-fold content. Flip them to eager as soon as their menu opens. */
    function loadLazyImages(root) {
      if (!root) return;
      root.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        if (!img.complete || img.naturalWidth === 0) {
          img.loading = 'eager';
          var src = img.currentSrc || img.src;
          if (src) {
            img.src = src;
          }
        }
      });
    }

    function closeAllCustomSelects(immediate) {
      header.querySelectorAll('[data-custom-select].oddit-header__custom-select--open').forEach(function (root) {
        if (typeof root._closeCustomSelect === 'function') {
          root._closeCustomSelect(immediate);
        }
      });
    }

    function initMobileCustomSelects() {
      header.querySelectorAll('[data-custom-select]').forEach(function (root) {
        if (root.dataset.customSelectInit === 'true') return;
        root.dataset.customSelectInit = 'true';

        var select = root.querySelector('.oddit-header__custom-select-native');
        var trigger = root.querySelector('.oddit-header__custom-select-trigger');
        var valueEl = root.querySelector('.oddit-header__custom-select-value');
        var list = root.querySelector('.oddit-header__custom-select-list');
        if (!select || !trigger || !valueEl || !list) return;

        function updateTriggerLabel() {
          var opt = select.options[select.selectedIndex];
          valueEl.textContent = opt ? opt.textContent.trim() : '';
        }

        function buildList() {
          list.innerHTML = '';
          Array.from(select.options).forEach(function (opt) {
            var li = document.createElement('li');
            li.className = 'oddit-header__custom-select-option';
            li.setAttribute('role', 'option');
            li.dataset.value = opt.value;
            li.textContent = opt.textContent.trim();
            li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
            if (opt.selected) li.classList.add('is-selected');
            list.appendChild(li);
          });
        }

        function positionList() {
          var gap = 4;
          var padding = 8;
          var maxListHeight = 250;
          var triggerRect = trigger.getBoundingClientRect();
          var spaceAbove = triggerRect.top - padding - gap;

          list.style.maxHeight = Math.min(maxListHeight, spaceAbove) + 'px';
        }

        function finishClose() {
          if (list._closeTimer) {
            clearTimeout(list._closeTimer);
            list._closeTimer = null;
          }
          list.hidden = true;
          list.dataset.state = '';
          list.style.maxHeight = '';
          trigger.setAttribute('aria-expanded', 'false');
          root.classList.remove('oddit-header__custom-select--open');
        }

        function close(immediate) {
          if (list.hidden) return;
          if (immediate || customSelectReducedMotion) {
            finishClose();
            return;
          }
          if (list.dataset.state === 'closed') return;

          list.dataset.state = 'closed';
          trigger.setAttribute('aria-expanded', 'false');
          root.classList.remove('oddit-header__custom-select--open');

          list.addEventListener(
            'animationend',
            function onCloseEnd(e) {
              if (e.target !== list) return;
              list.removeEventListener('animationend', onCloseEnd);
              finishClose();
            }
          );

          list._closeTimer = setTimeout(finishClose, 180);
        }

        function open() {
          closeAllCustomSelects(true);
          buildList();
          positionList();
          list.hidden = false;
          list.dataset.state = '';
          trigger.setAttribute('aria-expanded', 'true');
          root.classList.add('oddit-header__custom-select--open');

          if (customSelectReducedMotion) {
            list.dataset.state = 'open';
            return;
          }

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              if (root.classList.contains('oddit-header__custom-select--open')) {
                list.dataset.state = 'open';
              }
            });
          });
        }

        function selectValue(value) {
          if (select.value === value) {
            close(true);
            return;
          }
          select.value = value;
          updateTriggerLabel();
          buildList();
          close(true);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        buildList();
        updateTriggerLabel();

        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (root.classList.contains('oddit-header__custom-select--open')) {
            close(false);
          } else {
            open();
          }
        });

        list.addEventListener('click', function (e) {
          var option = e.target.closest('.oddit-header__custom-select-option');
          if (!option) return;
          selectValue(option.dataset.value);
        });

        var drawer = root.closest('.oddit-header__mobile-menu-drawer');
        if (drawer) {
          drawer.addEventListener('scroll', function () {
            close(true);
          }, { passive: true });
        }

        root._closeCustomSelect = close;
        root._repositionCustomSelect = function () {
          if (root.classList.contains('oddit-header__custom-select--open')) {
            positionList();
          }
        };
      });

      if (!customSelectGlobalListenersBound) {
        customSelectGlobalListenersBound = true;

        document.addEventListener('click', function (e) {
          if (e.target.closest('[data-custom-select]')) return;
          closeAllCustomSelects(false);
        });

        window.addEventListener('resize', function () {
          document.querySelectorAll('[data-custom-select].oddit-header__custom-select--open').forEach(function (root) {
            if (typeof root._repositionCustomSelect === 'function') {
              root._repositionCustomSelect();
            }
          });
        }, { passive: true });
      }
    }

    function measureHeaderSpacePx() {
      var measureEl = headerInner || header;
      var rect = measureEl.getBoundingClientRect();
      if (rect.height === 0) return null;
      return Math.round(rect.bottom);
    }

    function syncMobileMenuLayout() {
      if (!mobileMenu || !headerInner) return;
      if (mobileMenu.getAttribute('aria-hidden') !== 'false') return;

      var bottom = headerInner.getBoundingClientRect().bottom;
      var height = Math.max(0, Math.round(window.innerHeight - bottom));
      mobileMenu.style.height = height + 'px';
    }

    function updateHeaderSpace(force) {
      if (headerSpaceRaf && !force) return;

      headerSpaceRaf = requestAnimationFrame(function () {
        headerSpaceRaf = null;

        var headerSpace = measureHeaderSpacePx();
        if (headerSpace === null) return;
        if (!force && headerSpace === lastHeaderSpace) return;

        lastHeaderSpace = headerSpace;
        document.documentElement.style.setProperty('--s-header-space', headerSpace + 'px');
        syncMobileMenuLayout();
      });
    }

    function onHeaderLayoutChange() {
      updateHeaderSpace(false);
      syncMobileMenuLayout();
    }

    function setupHeaderSpace() {
      updateHeaderSpace(true);
      window.addEventListener('scroll', onHeaderLayoutChange, { passive: true });
      window.addEventListener('resize', onHeaderLayoutChange, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        var headerSpaceObserver = new ResizeObserver(onHeaderLayoutChange);
        if (headerInner) headerSpaceObserver.observe(headerInner);
        headerSpaceObserver.observe(header);

        if (headerGroup) headerSpaceObserver.observe(headerGroup);

        var announcementBar = document.querySelector('.oddit-announcement-bar');
        if (announcementBar) headerSpaceObserver.observe(announcementBar);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupHeaderSpace);
    } else {
      setupHeaderSpace();
    }

    function syncMobileShippingText() {
      var mobileShippingText = header.querySelector('[data-oddit-mobile-shipping-text]');
      if (!mobileShippingText) return;

      var activeText = document.querySelector(
        '.oddit-announcement-bar__inner.is-active [data-oddit-announcement-text]'
      );
      if (activeText) {
        var activeContent = activeText.textContent.replace(/\s+/g, ' ').trim();
        if (/shipping/i.test(activeContent)) {
          mobileShippingText.textContent = activeContent;
          return;
        }
      }

      var announcementTexts = document.querySelectorAll(
        '.oddit-announcement-bar [data-oddit-announcement-text]'
      );

      for (var i = 0; i < announcementTexts.length; i++) {
        var text = announcementTexts[i].textContent.replace(/\s+/g, ' ').trim();
        if (/shipping/i.test(text)) {
          mobileShippingText.textContent = text;
          return;
        }
      }
    }

    syncMobileShippingText();
    document.addEventListener('oddit-announcement:change', syncMobileShippingText);

    /* ---------- Mobile Menu ---------- */

    function resetMobilePanels() {
      if (mobileRootPanel) mobileRootPanel.removeAttribute('aria-hidden');
      mobileChildPanels.forEach(function (panel) {
        panel.setAttribute('aria-hidden', 'true');
      });
      mobilePanelTriggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
      lastMobilePanelTrigger = null;
    }

    function openMobilePanel(panelId, trigger) {
      var panel = header.querySelector('[data-mobile-panel="' + panelId + '"]');
      if (!panel || !mobileRootPanel) return;

      mobileRootPanel.setAttribute('aria-hidden', 'true');
      mobileChildPanels.forEach(function (childPanel) {
        childPanel.setAttribute('aria-hidden', childPanel === panel ? 'false' : 'true');
      });
      mobilePanelTriggers.forEach(function (panelTrigger) {
        panelTrigger.setAttribute('aria-expanded', panelTrigger === trigger ? 'true' : 'false');
      });
      lastMobilePanelTrigger = trigger;

      var firstFocusable = panel.querySelector('button, a, select, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus({ preventScroll: true });
      loadLazyImages(panel);
    }

    function closeMobilePanel() {
      if (!mobileRootPanel) return;

      mobileRootPanel.removeAttribute('aria-hidden');
      mobileChildPanels.forEach(function (panel) {
        panel.setAttribute('aria-hidden', 'true');
      });
      mobilePanelTriggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });

      if (lastMobilePanelTrigger) {
        lastMobilePanelTrigger.focus({ preventScroll: true });
      }
      lastMobilePanelTrigger = null;
    }

    function openMobileMenu() {
      if (!mobileMenu) return;
      /* If the search drawer is currently open, dismiss it first — the user
         is switching contexts to the mobile nav, no need to keep search up. */
      if (document.documentElement.classList.contains('is-search-active')) {
        var headerSearchToggleEl = header.querySelector('.js-search-toggle');
        if (headerSearchToggleEl) headerSearchToggleEl.click();
      }
      loadLazyImages(mobileRootPanel);
      mobileMenu.setAttribute('aria-hidden', 'false');
      resetMobilePanels();
      if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', 'true');
        menuToggle.setAttribute('aria-label', 'Close menu');
      }
      document.documentElement.classList.add('is-oddit-mobile-menu-open');
      updateHeaderSpace(true);
      requestAnimationFrame(syncMobileMenuLayout);
    }

    function closeMobileMenu() {
      if (!mobileMenu) return;
      closeAllCustomSelects(true);
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileMenu.style.height = '';
      if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', 'false');
        menuToggle.setAttribute('aria-label', 'Open menu');
      }
      resetMobilePanels();
      document.documentElement.classList.remove('is-oddit-mobile-menu-open');
      /* If the global search drawer is layered on top of the mobile nav,
         dismiss it together — closing the nav underneath shouldn't leave
         a stranded search overlay. */
      if (document.documentElement.classList.contains('is-search-active')) {
        var headerSearchToggleEl = header.querySelector('.js-search-toggle');
        if (headerSearchToggleEl) headerSearchToggleEl.click();
      }
    }

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', function () {
        var isOpen = mobileMenu.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          closeMobileMenu();
        } else {
          openMobileMenu();
        }
      });
    }

    if (mobileOverlay) {
      mobileOverlay.addEventListener('click', closeMobileMenu);
    }

    mobilePanelTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        openMobilePanel(trigger.getAttribute('data-mobile-panel-trigger'), trigger);
      });
    });

    mobilePanelBackButtons.forEach(function (button) {
      button.addEventListener('click', closeMobilePanel);
    });

    var headerSearchToggle = header.querySelector('.js-search-toggle');

    function getSearchInput() {
      /* The legacy g-header clones the entire .js-g-search panel into its
         hidden mobile menu (`.js-mobile-menu-inner`). That clone comes
         FIRST in DOM order, so a plain `.js-g-search .c-search-form__input`
         selector lands on the invisible (zero-size) cloned input. Pick the
         REAL panel — the one not nested inside the legacy mobile menu. */
      var panels = document.querySelectorAll('.js-g-search');
      for (var i = 0; i < panels.length; i++) {
        if (!panels[i].closest('.js-mobile-menu')) {
          return panels[i].querySelector('.c-search-form__input');
        }
      }
      return null;
    }

    /* The theme's `ariaFocusManager` watches for `aria-hidden` and proactively
       sets tabindex="-1" on every focusable child of any [aria-hidden="true"]
       element. Because the search panel ships with aria-hidden="true", the
       input is born unfocusable. When the panel opens we get the tabindex
       reset back via a MutationObserver — but that's async, so any
       synchronous .focus() we make right after opening lands on an element
       that's still tabindex="-1" and the call no-ops on some browsers.
       Force-reset it ourselves before calling focus(). */
    function focusSearchInput() {
      var input = getSearchInput();
      if (!input) return;
      if (input.getAttribute('tabindex') === '-1') input.removeAttribute('tabindex');
      input.focus({ preventScroll: true });
    }

    /* Auto-focus the search input every time the search drawer opens.
       gSearch.searchOpen() only auto-focuses on >=601px, so phone users
       (including Chrome DevTools mobile emulation) never get a cursor in
       the field. Rather than racing the open handler, we observe the
       `is-search-active` class on <html> — the moment it appears, we
       focus the input. We also try a couple of follow-ups on the next
       frames because the theme's `ariaFocusManager` may stomp on tabindex
       in a microtask. */
    var rootEl = document.documentElement;
    var lastSearchActive = rootEl.classList.contains('is-search-active');
    var rootObserver = new MutationObserver(function () {
      var nowActive = rootEl.classList.contains('is-search-active');
      if (nowActive && !lastSearchActive) {
        focusSearchInput();
        requestAnimationFrame(focusSearchInput);
      }
      lastSearchActive = nowActive;
    });
    rootObserver.observe(rootEl, { attributes: true, attributeFilter: ['class'] });

    /* The theme's mobile search "Close" button (.js-search-close-mobile, rendered
       inside c-search-form) only toggles an internal class without actually
       closing the search drawer. Forward its click to the global search toggle
       so it dismisses the search the same way the overlay/Esc do. */
    if (headerSearchToggle) {
      document.addEventListener('click', function (e) {
        var closeBtn = e.target.closest && e.target.closest('.js-search-close-mobile');
        if (!closeBtn) return;
        if (document.documentElement.classList.contains('is-search-active')) {
          headerSearchToggle.click();
        }
      });
    }

    /* ---------- Desktop Mega Menu Dropdowns ---------- */
    var dropdownItems = header.querySelectorAll('.oddit-header__nav-item--has-dropdown');
    var dropdownButtons = header.querySelectorAll('.oddit-header__nav-link--dropdown');
    var megaCloseTimer = null;

    function cancelMegaCloseTimer() {
      if (megaCloseTimer) {
        clearTimeout(megaCloseTimer);
        megaCloseTimer = null;
      }
    }

    function scheduleMegaClose() {
      cancelMegaCloseTimer();
      megaCloseTimer = setTimeout(function () {
        megaCloseTimer = null;
        closeAllMegaMenus();
      }, 150);
    }

    function closeAllMegaMenus() {
      cancelMegaCloseTimer();
      dropdownItems.forEach(function (li) {
        var btn = li.querySelector('.oddit-header__nav-link--dropdown');
        var megaMenu = li.querySelector('.oddit-header__mega-menu');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (megaMenu) megaMenu.setAttribute('aria-hidden', 'true');
      });
    }

    function openMegaMenuFor(li) {
      var btn = li.querySelector('.oddit-header__nav-link--dropdown');
      var megaMenu = li.querySelector('.oddit-header__mega-menu');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      loadLazyImages(megaMenu);
      if (megaMenu) megaMenu.setAttribute('aria-hidden', 'false');
    }

    /* Hover on fine pointers; click-to-toggle on touch / no-hover devices */
    var prefersHover = window.matchMedia('(hover: hover)').matches;

    if (prefersHover) {
      dropdownItems.forEach(function (li) {
        li.addEventListener('mouseenter', function () {
          cancelMegaCloseTimer();
          closeAllMegaMenus();
          openMegaMenuFor(li);
        });
        li.addEventListener('mouseleave', function () {
          scheduleMegaClose();
        });
        li.addEventListener('focusin', function () {
          cancelMegaCloseTimer();
          closeAllMegaMenus();
          openMegaMenuFor(li);
        });
        li.addEventListener('focusout', function (e) {
          if (!li.contains(e.relatedTarget)) {
            closeAllMegaMenus();
          }
        });
      });
    } else {
      dropdownButtons.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          var li = btn.closest('.oddit-header__nav-item--has-dropdown');
          var isExpanded = btn.getAttribute('aria-expanded') === 'true';
          closeAllMegaMenus();
          if (!isExpanded && li) {
            openMegaMenuFor(li);
          }
        });
      });
    }

    /* Close mega menus when clicking outside */
    document.addEventListener('click', function (e) {
      if (!header.contains(e.target)) {
        closeAllMegaMenus();
      }
    });

    /* Close mega menus on Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (header.querySelector('[data-custom-select].oddit-header__custom-select--open')) {
          closeAllCustomSelects(false);
          return;
        }
        closeAllMegaMenus();
        var openChildPanel = header.querySelector('.oddit-header__mobile-panel--child[aria-hidden="false"]');
        if (openChildPanel && mobileMenu && mobileMenu.getAttribute('aria-hidden') === 'false') {
          closeMobilePanel();
        } else {
          closeMobileMenu();
        }
      }
    });

    /* Close mega menus when mouse leaves the header area */
    var nav = header.querySelector('.oddit-header__nav');
    if (nav) {
      header.addEventListener('mouseleave', function () {
        closeAllMegaMenus();
      });
    }

    initMobileCustomSelects();
  }

  /* ---------- Initialize ---------- */
  initHeader();

  /* ---------- Theme Editor Compatibility ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (section && section.querySelector('.oddit-header')) {
      initHeader(section.querySelector('.oddit-header'));
    }
  });
})();
